import { useQuery } from '@apollo/client';
import { MouseEvent, useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';

import TextWithIcon from '@components/text/TextWithIcon';
import useLazyQueryWithPromise from '@hooks/useLazyQueryWithPromise';
import {
  AttachmentFragment,
  GetAttachmentDocument,
  GetAttachmentDownloadDocument,
  GetAttachmentDownloadQuery,
  GetAttachmentDownloadQueryVariables,
  GetAttachmentQuery,
  GetAttachmentQueryVariables
} from '@typing/Generated';
import { downloadFile } from '@utils/downloadUtils';

type Props = {
  attachment: AttachmentFragment;
};

const Attachment = ({ attachment }: Props) => {
  const intl = useIntl();

  const getAttachmentQuery = useQuery<GetAttachmentQuery, GetAttachmentQueryVariables>(GetAttachmentDocument, {
    skip: !attachment.scanning,
    variables: { id: attachment.id }
  });

  const attachmentFetch = useLazyQueryWithPromise<GetAttachmentDownloadQuery, GetAttachmentDownloadQueryVariables>(
    GetAttachmentDownloadDocument
  );

  const handleDownload = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      attachmentFetch({ fetchPolicy: 'no-cache', variables: { id: attachment.id } }).then(response => {
        if (response.data.attachmentDownload.url) {
          downloadFile(response.data.attachmentDownload.url, attachment.fileName);
        }
      });
    },
    [attachment.fileName, attachment.id, attachmentFetch]
  );

  useEffect(() => {
    if (attachment.scanning) {
      getAttachmentQuery.startPolling(5000);
    } else {
      getAttachmentQuery.stopPolling();
    }

    return () => {
      getAttachmentQuery.stopPolling();
    };
  }, [attachment, getAttachmentQuery]);

  return (
    <>
      {attachment.scanning ? (
        <TextWithIcon icon="spinner" iconFirst text={intl.formatMessage({ id: 'dictionary.scanning' })} />
      ) : (
        <a onClick={handleDownload}>
          <TextWithIcon icon="file" iconFirst text={attachment.fileName} />
        </a>
      )}
    </>
  );
};

export default Attachment;
